// MARK://dhk8/admin-shell/components/audit-format
// Helpers de formato COMPARTIDOS entre AuditTrail (historial por ficha) y AuditPage
// (feed global) — para no duplicar la lógica de presentación del log de auditoría.
//   · formatTimestamp(ts)  → fecha + hora corta local (mismo formato que InventoryMovements).
//   · humanizeEvent(name)  → { action, domain } a partir de un evento punteado
//                            (ej. "dhk8.erp.warehouse.updated" → action "warehouse · updated", domain "erp").
//   · renderChanges(payload) → ReactNode compacto de "qué cambió" a partir del JSON crudo.
//   · severityTone(sev)    → StatusTone para el StatusPill (high→danger, medium→warn, low→info).
//   · severityLabel(sev)   → etiqueta ES de la severidad.
import { createElement, type ReactNode } from 'react'
import type { StatusTone } from './StatusPill.js'

// ── Cuándo ───────────────────────────────────────────────────────────────────
/** Fecha + hora corta local. Mismo formato que InventoryMovementsPage. */
export function formatTimestamp(ts: string): string {
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ts
  return d.toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

// ── Acción ───────────────────────────────────────────────────────────────────
export interface HumanEvent {
  /** Los 2 últimos segmentos unidos con " · " (ej. "warehouse · updated"). */
  action: string
  /** Dominio (segmento previo a los 2 últimos, ej. "erp"); null si no hay. */
  domain: string | null
}

/**
 * Humaniza un eventName punteado. Toma los 2 últimos segmentos como la acción y el
 * segmento inmediatamente anterior como el dominio (erp/catalog/pos…). Tolera nombres
 * con menos de 3 segmentos.
 */
export function humanizeEvent(eventName: string): HumanEvent {
  const parts = (eventName ?? '').split('.').filter(Boolean)
  if (parts.length === 0) return { action: eventName || '—', domain: null }
  const last2 = parts.slice(-2)
  const action = last2.join(' · ')
  const domain = parts.length >= 3 ? parts[parts.length - 3] ?? null : null
  return { action, domain }
}

// ── Severidad ──────────────────────────────────────────────────────────────────
/** high→danger, medium→warn, low→info; cualquier otra → neutral. */
export function severityTone(severity: string): StatusTone {
  switch ((severity ?? '').toLowerCase()) {
    case 'high':
    case 'critical':
      return 'danger'
    case 'medium':
    case 'warning':
    case 'warn':
      return 'warn'
    case 'low':
    case 'info':
      return 'info'
    default:
      return 'neutral'
  }
}

const SEVERITY_LABEL: Record<string, string> = { high: 'Alta', medium: 'Media', low: 'Baja' }
export function severityLabel(severity: string): string {
  const k = (severity ?? '').toLowerCase()
  return SEVERITY_LABEL[k] ?? (severity || '—')
}

// ── Qué cambió ───────────────────────────────────────────────────────────────
const MAX_CHANGES = 4
// Claves que NO sirven como resumen (ids largos, ruido técnico).
const NOISE_KEYS = new Set(['id', 'tenantid', 'tenant_id', 'aggregateid', 'aggregate_id', 'networkid', 'network_id', 'createdat', 'updatedat', 'created_at', 'updated_at'])

/** Representa un valor escalar/compuesto de forma corta y legible. */
function shortValue(v: unknown): string {
  if (v === null || v === undefined) return '∅'
  if (typeof v === 'string') return v.length > 40 ? v.slice(0, 39) + '…' : (v || '""')
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  try {
    const s = JSON.stringify(v)
    return s.length > 40 ? s.slice(0, 39) + '…' : s
  } catch {
    return String(v)
  }
}

function looksLikeId(v: unknown): boolean {
  return typeof v === 'string' && v.length >= 16 && !/\s/.test(v)
}

function isChangePair(v: unknown): v is { before?: unknown; after?: unknown } {
  return typeof v === 'object' && v !== null && ('before' in v || 'after' in v)
}

/**
 * Render compacto de "qué cambió" desde el payload JSON crudo:
 *   1) payload.changes = { campo: { before, after } } → "campo: before → after" (máx 4 + "…")
 *   2) payload.fields  = string[] → lista de campos
 *   3) si no, resumen tenue de 1-2 claves relevantes (excluye ids largos) o "—"
 * Defensivo: si JSON.parse falla, muestra el payload crudo truncado.
 */
export function renderChanges(payload: string): ReactNode {
  let data: unknown
  try {
    data = JSON.parse(payload)
  } catch {
    const raw = payload?.length > 80 ? payload.slice(0, 79) + '…' : payload
    return createElement('span', { className: 'adm-muted', title: payload }, raw || '—')
  }

  if (typeof data !== 'object' || data === null) {
    return createElement('span', { className: 'adm-muted' }, shortValue(data))
  }
  const obj = data as Record<string, unknown>

  // (1) changes: { campo: { before, after } }
  const changes = obj['changes']
  if (changes && typeof changes === 'object') {
    const entries = Object.entries(changes as Record<string, unknown>)
    if (entries.length > 0) {
      const shown = entries.slice(0, MAX_CHANGES)
      const lines = shown.map(([field, val]) => {
        if (isChangePair(val)) {
          return createElement(
            'div',
            { key: field, className: 'adm-audit-change' },
            createElement('span', { className: 'adm-audit-change__field' }, field),
            ': ',
            createElement('span', { className: 'adm-muted' }, shortValue(val.before)),
            ' → ',
            createElement('span', null, shortValue(val.after)),
          )
        }
        return createElement(
          'div',
          { key: field, className: 'adm-audit-change' },
          createElement('span', { className: 'adm-audit-change__field' }, field),
          ': ',
          createElement('span', null, shortValue(val)),
        )
      })
      if (entries.length > MAX_CHANGES) {
        lines.push(createElement('div', { key: '__more', className: 'adm-muted' }, `… +${entries.length - MAX_CHANGES} más`))
      }
      return createElement('div', { className: 'adm-audit-changes' }, ...lines)
    }
  }

  // (2) fields: string[]
  const fields = obj['fields']
  if (Array.isArray(fields) && fields.length > 0) {
    const names = fields.filter((f): f is string => typeof f === 'string')
    if (names.length > 0) {
      const shown = names.slice(0, MAX_CHANGES + 2)
      const label = shown.join(', ') + (names.length > shown.length ? `, +${names.length - shown.length}` : '')
      return createElement('span', null, label)
    }
  }

  // (3) resumen tenue de 1-2 claves relevantes (excluye ids largos / ruido)
  const relevant = Object.entries(obj)
    .filter(([k, v]) => !NOISE_KEYS.has(k.toLowerCase()) && !looksLikeId(v) && typeof v !== 'object')
    .slice(0, 2)
  if (relevant.length > 0) {
    const label = relevant.map(([k, v]) => `${k}: ${shortValue(v)}`).join(' · ')
    return createElement('span', { className: 'adm-muted' }, label)
  }

  return createElement('span', { className: 'adm-muted' }, '—')
}

/** Acorta un id para mostrarlo (primeros 8). Vacío → "—". */
export function shortId(id: string | null | undefined): string {
  if (!id) return '—'
  return id.length > 8 ? id.slice(0, 8) : id
}
